import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Clock, AlertCircle, CalendarClock } from 'lucide-react';
import {
  buildSubscriptionHighlights,
  demandLabel,
  describeNextOpenings,
  formatMultiple,
} from '../../utils/subscriptionMapper';
import { formatDayShort } from '../../utils/ipoCalendar';

const getHeaderBadge = (accentColor, label) => {
  switch (accentColor) {
    case 'amber':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200/80 px-2 py-0.5 rounded uppercase tracking-wider">
          <Clock className="w-3 h-3 text-amber-600" />
          <span>{label}</span>
        </span>
      );
    case 'emerald':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded uppercase tracking-wider">
          <AlertCircle className="w-3 h-3 text-emerald-600" />
          <span>{label}</span>
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200/80 px-2 py-0.5 rounded uppercase tracking-wider">
          <TrendingUp className="w-3 h-3 text-indigo-600" />
          <span>{label}</span>
        </span>
      );
  }
};

const cardSubtitle = ({ key, row }) => {
  if (key === 'closing' || row.phase === 'closing_today') return `Final Day Bidding • ${row.boardLabel} Issue`;
  if (!row.closeOn) return `${row.boardLabel} Issue`;
  return `${row.boardLabel} Issue • ${row.phase === 'closed' ? 'Closed' : 'Closes'} ${formatDayShort(row.closeOn)}`;
};

export const SubscriptionHighlight = ({ rows, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 my-6 sm:my-8">
        {Array.from({ length: 3 }).map((_, idx) => (
          <div key={idx} className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs animate-pulse">
            <div className="w-36 h-5 bg-slate-200 rounded" />
            <div className="w-48 h-5 bg-slate-200 rounded mt-4" />
            <div className="w-32 h-3 bg-slate-100 rounded mt-2" />
            <div className="w-24 h-8 bg-slate-200 rounded mt-6" />
          </div>
        ))}
      </div>
    );
  }

  const cards = buildSubscriptionHighlights(rows);

  if (cards.length === 0) {
    const nextOpenings = describeNextOpenings(rows);
    if (!nextOpenings) return <div className="my-6 sm:my-8" />;
    return (
      <div className="my-6 sm:my-8 flex items-start gap-3 bg-white rounded-2xl border border-slate-200/90 p-4 sm:p-5 shadow-xs">
        <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center flex-shrink-0">
          <CalendarClock className="w-4 h-4" />
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900">Bidding hasn't opened yet for the IPOs being tracked</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {nextOpenings}. Subscription multiples appear here once bidding starts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 my-6 sm:my-8">
      {cards.map((card) => {
        const { row } = card;
        const headline = card.metric === 'retail' ? row.retail : row.total;
        return (
          <div
            key={card.key}
            className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs hover:border-indigo-300 hover:shadow-card transition-all"
          >
            {/* Top pill */}
            <div className="flex items-center justify-between">
              {getHeaderBadge(card.accent, card.label)}
            </div>

            {/* Company Title + Subtitle */}
            <div className="mt-3">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-tight truncate" title={row.company}>
                {row.symbol ? (
                <Link to={`/ipo-subscription/${encodeURIComponent(row.symbol)}`} className="hover:text-[#008080]">
                  {row.company}
                </Link>
              ) : (
                row.company
              )}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">{cardSubtitle(card)}</p>
            </div>

            {/* Multiple Stat */}
            <div className="mt-4 flex items-baseline justify-between border-t border-slate-100 pt-3">
              <div>
                <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
                  {formatMultiple(headline)}
                </div>
                <div className="text-xs font-semibold text-indigo-600">
                  {card.metric === 'retail' ? 'Retail (RII) Quota' : demandLabel(row.total)}
                </div>
              </div>

              {/* Quota breakdown pills */}
              <div className="text-right text-[11px] space-y-0.5 text-slate-500">
                <div>Retail: <strong className="text-slate-800">{formatMultiple(row.retail)}</strong></div>
                <div>QIB: <strong className="text-slate-800">{formatMultiple(row.qib)}</strong></div>
                <div>NII: <strong className="text-slate-800">{formatMultiple(row.nii)}</strong></div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SubscriptionHighlight;
